import React, { useEffect, useState } from 'react';
import { Box, Typography, Avatar, Checkbox } from '@mui/material';
import { VisibilityOutlinedIcon, ContactsOutlinedIcon, SurroundSoundOutlinedIcon } from 'icons/materialIcons';
import placeholderImage from 'images/placeholder-grey-image.jpg'; // Adjust path as needed
import avatarPlaceholder from 'images/avatar-cropped.png'; // Adjust path as needed
import { lighten } from '@mui/system'; // Import the lighten utility


const TeamV2CardComponent = ({ card, onEditCardClick, isSelected = false, onSelectionChange = () => { } }) => {
    const [fullName, setFullName] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [company, setCompany] = useState('');
    const [coverPhotoUrl, setCoverPhotoUrl] = useState(null);
    const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
    const [profilePictureUrl, setProfilePictureUrl] = useState(null);
    const [cardTheme, setCardTheme] = useState('');
    const [cardName, setCardName] = useState('');
    const lightenedCardTheme = cardTheme ? lighten(cardTheme, 0.7) : '#fff';
    // Metrics (views, leads, devices) — fallbacks if not provided on the card
    const viewsCount = card?.metrics?.views ?? card?.views ?? 0;
    const leadsCount = card?.metrics?.leads ?? card?.leads ?? 0;
    const devicesCount = card?.metrics?.devices ?? card?.devices ?? 0;



    useEffect(() => {
        if (card) {
            setFullName(card.contact_info?.fullName || '');
            setCardName(card.cardName || '');
            setJobTitle(card.contact_info?.jobTitle || '');
            setCompany(card.contact_info?.company || '');
            setCoverPhotoUrl(card.coverPhotoUrl || null);
            setCompanyLogoUrl(card.companyLogoUrl || null);
            setProfilePictureUrl(card.profilePictureUrl || null);
            setCardTheme(card.cardTheme || '');
        }
    }, [card]);

    // Removed share and edit button handlers for simplified metrics UI

    const getJobTitleAndCompany = () => {
        if (jobTitle && company) {
            return `${jobTitle} at ${company}`;
        }
        return jobTitle || company || '';
    };

    const getEmail = () => {
        return card?.contact_info?.email || card?.email || '';
    };

    return (
        <Box
            onClick={() => onEditCardClick(card)}
            sx={{
                cursor: 'pointer',
                padding: '0.3125rem',
                backgroundColor: lightenedCardTheme || '#fff',
                borderRadius: '0.625rem',
                boxShadow: '0 0.25rem 0.5rem rgba(0, 0, 0, 0.1)',
                width: '100%',
                maxWidth: '32%',
                minHeight: '16.25rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                boxSizing: 'border-box',
                marginRight: '1rem',
                marginBottom: '1rem',
                position: 'relative',
            }}
        >
            {/* Checkbox in top-left corner */}
            <Checkbox
                checked={isSelected}
                onChange={(e) => {
                    e.stopPropagation();
                    onSelectionChange(e.target.checked);
                }}
                sx={{
                    position: 'absolute',
                    top: '0.5rem',
                    left: '0.5rem',
                    zIndex: 1,
                    color: '#e0e0e0',
                    padding: '0',
                    margin: '0',
                    '&.Mui-checked': {
                        color: '#000',
                    },
                    '& .MuiSvgIcon-root': {
                        borderRadius: '0.5rem',
                    },
                }}
            />
            <Box
                sx={{
                    width: '100%',
                    height: '7.5rem',
                    backgroundImage: `url(${coverPhotoUrl || placeholderImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderTopLeftRadius: '0.625rem',
                    borderTopRightRadius: '0.625rem',
                    borderBottomLeftRadius: '0.625rem',
                    borderBottomRightRadius: '0.625rem',
                    position: 'relative',
                    marginBottom: '0.75rem',
                }}
            >
                <Avatar
                    src={profilePictureUrl || avatarPlaceholder}
                    sx={{
                        width: '6rem',
                        height: '6rem',
                        backgroundColor: '#ffffff',
                        position: 'absolute',
                        bottom: '-2.5rem',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        border: '0.25rem solid #ffffff',
                    }}
                />

                {companyLogoUrl && (
                    <Avatar
                        src={companyLogoUrl}
                        sx={{
                            width: '2rem',
                            height: '2rem',
                            position: 'absolute',
                            bottom: '-1.625rem', // Adjusts position relative to profile picture
                            right: '40%', // Adjusts position relative to profile picture
                            transform: 'translateX(50%) translateY(25%)', // Positions logo slightly overlapping profile picture
                            backgroundColor: '#ffffff',
                        }}
                    />
                )}
            </Box>

            <Typography
                variant="h6"
                sx={{
                    marginTop: '2.25rem',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    color: '#000',
                    marginBottom: '0.25rem',
                }}
            >
                {fullName || cardName || 'Card Title'}
            </Typography>

            <Typography
                variant="body2"
                sx={{
                    fontSize: '0.8rem',
                    color: '#555',
                    fontWeight: 600,
                    textAlign: 'center',
                    marginBottom: '0.375rem',
                }}
            >
                {getJobTitleAndCompany()}
            </Typography>

            {getEmail() && (
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '0.75rem',
                        color: '#888',
                        fontWeight: 500,
                        textAlign: 'center',
                        marginBottom: '0.75rem',
                        wordBreak: 'break-all'
                    }}
                >
                    {getEmail()}
                </Typography>
            )}

            {/* Metrics Row */}
            <Box sx={{
                display: 'flex',
                gap: { xs: '1.5rem', sm: '2rem' },
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '1rem',
                marginBottom: { xs: '1rem', sm: '1.375rem' }
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <VisibilityOutlinedIcon sx={{ fontSize: '1.125rem', color: '#777' }} />
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#333', fontWeight: 600 }}>
                        {viewsCount}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <ContactsOutlinedIcon sx={{ fontSize: '1.125rem', color: '#777' }} />
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#333', fontWeight: 600 }}>
                        {leadsCount}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <SurroundSoundOutlinedIcon sx={{ fontSize: '1.125rem', color: '#777' }} />
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#333', fontWeight: 600 }}>
                        {devicesCount}
                    </Typography>
                </Box>
            </Box>


        </Box>
    );
};

export default TeamV2CardComponent;
